import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Settings2, Check, X, Info } from "lucide-react";
import ProfileTab from "@/components/settings/tabs/ProfileTab";
import PreferencesTab from "@/components/settings/tabs/PreferencesTab";
import APIKeysTab from "@/components/settings/tabs/APIKeysTab";
import NotificationsTab from "@/components/settings/tabs/NotificationsTab";
import CreditsTab from "@/components/settings/tabs/CreditsTab";
import AdminTab from "@/components/settings/tabs/AdminTab";
import LLMControlsTab from "@/components/settings/tabs/LLMControlsTab";
import SettingsTabsList from "@/components/settings/SettingsTabsList";
import { useToast } from "@/components/ui/use-toast";
import { BloombergRadioButton } from '@/components/trading/BloombergRadioButton';
import { supabase } from '@/integrations/supabase/client';

// Admin email list - in production, this should be configured via environment variables
const ADMIN_EMAILS = [
  'es@papartners.co',
  'team@stewietrader.com',
  'support@stewietrader.com'
];

export default function Settings() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Get selected tab from location state or URL parameter
  const getInitialTab = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    
    if (location.state?.tab) {
      return location.state.tab;
    } else if (tabParam) {
      return tabParam;
    }
    
    return "profile";
  };
  
  const [selectedTab, setSelectedTab] = useState(getInitialTab());
  const [isLoading, setIsLoading] = useState(true);
  const [purchaseStatus, setPurchaseStatus] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Check if user is admin
  useEffect(() => {
    console.log('🚀 Settings useEffect triggered - checking admin status...');
    
    const checkAdminStatus = async () => {
      try {
        console.log('📡 Calling supabase.auth.getUser()...');
        const { data: { user } } = await supabase.auth.getUser();
        
        console.log('👤 Supabase user data:', user);
        
        if (user?.email) {
          const userEmail = user.email.toLowerCase();
          const userIsAdmin = ADMIN_EMAILS.includes(userEmail);
          
          // Debug logging
          console.log('🔍 Admin Check Debug:');
          console.log('User email:', userEmail);
          console.log('Admin emails:', ADMIN_EMAILS);
          console.log('Is admin:', userIsAdmin);
          
          setIsAdmin(userIsAdmin);
          
          // If user navigated to admin tab but isn't admin, redirect to profile
          if (selectedTab === 'admin' && !userIsAdmin) {
            setSelectedTab('profile');
          }
        } else {
          console.log('❌ No user email found');
          console.log('User object:', user);
          setIsAdmin(false);
        }
      } catch (error) {
        console.error('💥 Error checking admin status:', error);
        setIsAdmin(false);
      }
    };
    
    checkAdminStatus();
  }, []); // Only run once on mount
  
  // Update URL when tab changes
  useEffect(() => {
    // Check for purchase status in URL
    const urlParams = new URLSearchParams(window.location.search);
    const purchaseParam = urlParams.get('purchase');
    
    if (purchaseParam) {
      setPurchaseStatus(purchaseParam);
      
      if (purchaseParam === 'success') {
        toast({
          title: "Purchase Successful",
          description: "Your credits have been added to your account.",
          variant: "default",
        });
      } else if (purchaseParam === 'canceled') {
        toast({
          title: "Purchase Canceled",
          description: "Your credit purchase was canceled.",
          variant: "destructive",
        });
      }
      
      // Remove the purchase parameter from URL to prevent duplicate toasts on refresh
      const newUrlParams = new URLSearchParams(urlParams);
      newUrlParams.delete('purchase');
      navigate(`/settings?${newUrlParams.toString()}`, { replace: true });
    }
    
    // Update URL with tab parameter when tab changes
    navigate(`/settings?tab=${selectedTab}`, { replace: true });
  }, [selectedTab, navigate, toast]);
  
  // Simulate loading state
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);
    
    return () => clearTimeout(timer);
  }, []);
  
  const handleTabChange = (tab: string) => {
    // Prevent non-admin users from accessing admin-only tabs
    if ((tab === 'admin' || tab === 'llm-controls') && !isAdmin) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to access admin settings.",
        variant: "destructive",
      });
      return;
    }
    
    setSelectedTab(tab);
  };
  
  return (
    <div className="min-h-screen py-8 px-4 md:px-6 lg:px-8 dark bg-stewie-dark animate-fade-in">
      <div className="max-w-6xl mx-auto">
        <header className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Settings2 className="h-6 w-6 text-stewie-blue" />
            <h1 className="text-3xl font-bold text-gradient">Settings</h1>
            {isAdmin && (
              <span className="bg-red-500/20 text-red-400 text-xs px-2 py-1 rounded-full">
                Admin Access
              </span>
            )}
          </div>
          <p className="text-stewie-gray">
            Configure your account preferences, API keys, and notification settings.
          </p>
        </header>
        
        <div className="grid grid-cols-1 lg:grid-cols-[250px_1fr] gap-8">
          {/* Sidebar with tabs */}
          <div className="glass-panel rounded-xl p-4">
            <SettingsTabsList 
              selectedTab={selectedTab} 
              onTabChange={handleTabChange}
              isAdmin={isAdmin}
            />
          </div>
          
          {/* Main content area */}
          <div className="glass-panel rounded-xl p-6">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-pulse flex flex-col items-center">
                  <div className="h-16 w-16 bg-stewie-blue/20 rounded-full mb-4"></div>
                  <div className="h-4 w-48 bg-stewie-blue/20 rounded mb-3"></div>
                  <div className="h-3 w-32 bg-stewie-blue/20 rounded"></div>
                </div>
              </div>
            ) : (
              <>
                {selectedTab === "profile" && <ProfileTab />}
                {selectedTab === "preferences" && <PreferencesTab />}
                {selectedTab === "api-keys" && <APIKeysTab />}
                {selectedTab === "notifications" && <NotificationsTab />}
                {selectedTab === "credits" && <CreditsTab />}
                {selectedTab === "llm-controls" && isAdmin && <LLMControlsTab />}
                {selectedTab === "admin" && isAdmin && <AdminTab />}
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Bloomberg Radio Button */}
      <BloombergRadioButton />
    </div>
  );
}
